import ErrorIcon from '@mui/icons-material/ErrorOutline'
import GifIcon from '@mui/icons-material/Gif'
import CircularProgress from '@mui/material/CircularProgress'
import { useEffect, useRef, useState } from 'react'
import { isVideoUrl } from 'utils/composer'
import styles from './GifPlayer.pcss'

const Freezeframe = require('freezeframe')

interface GifPlayerProps {
  gifUrl: string
  gifDataUrl?: string
  backgroundImage?: string
  loop?: boolean
  small?: boolean
  className?: string
  imageClassName?: string
  mediaElementId?: string
}

const LOADER_SIZE = 40
const LOADER_SIZE_SMALL = 24

function isGifUrl(url: string) {
  return url.toLowerCase().endsWith('.gif')
}

export function GifPlayer(props: GifPlayerProps) {
  let mediaUrl = props.gifUrl || props.gifDataUrl + ''
  // Remove query params from gif url
  if (props.gifUrl) {
    mediaUrl = mediaUrl.split('?')[0]
  }
  const isGif = props.gifDataUrl || isGifUrl(mediaUrl)
  const isVideo = isVideoUrl(mediaUrl)
  const invalidUrl = !isGif && !isVideo
  const videoRef = useRef<HTMLVideoElement>(null)
  const gifRef = useRef<HTMLDivElement>(null)
  const backgroundStyle = props.backgroundImage ? { backgroundImage: `url(${props.backgroundImage})` } : {}
  const [loaded, setLoaded] = useState(false)
  const onMediaLoaded = () => setLoaded(true)

  // Drive `loaded` from an off-DOM image preloader rather than the rendered
  // <img>'s onLoad. Freezeframe moves the <img> and renders a canvas (so the
  // element React owns is unreliable), and cached gifs fire `load` before
  // React attaches its handler. An off-DOM Image() avoids both — same approach
  // ProgressMedia uses. The browser cache means no extra download. Videos keep
  // using their own media events.
  useEffect(() => {
    setLoaded(false)

    if (invalidUrl || isVideo) {
      return
    }

    const preloader = new Image()
    preloader.onload = onMediaLoaded
    preloader.onerror = onMediaLoaded
    preloader.src = mediaUrl

    if (preloader.complete) {
      onMediaLoaded()
    }

    return () => {
      preloader.onload = null
      preloader.onerror = null
    }
  }, [mediaUrl, invalidUrl, isVideo])

  useEffect(() => {
    if (isGif && gifRef.current) {

      new Freezeframe(gifRef.current, { trigger: 'hover' })
    }
  }, [props.gifUrl, isGif])

  const playVideo = () => {
    const player = videoRef.current as HTMLVideoElement
    if (player) {
      player.play()
    }
  }

  const pauseVideo = () => {
    const player = videoRef.current as HTMLVideoElement
    if (player) {
      player.pause()
    }
  }

  const gifSign = !invalidUrl && (
    <div className={`${styles.center} ${styles['gif-icon-box']} ${props.small ? styles.sm : ''}`}>
      <GifIcon className={styles['gif-icon']} />
    </div>
  )

  return (
    <div className={`${styles.wrapper} ${props.className || ''}`}>
      {
        invalidUrl && (
          <div className={`${styles.error} ${styles.center}`}>
            <ErrorIcon className={styles.icon} />
            <p>Error loading media!</p>
          </div>
        )
      }
      {
        props.backgroundImage && (
          <div className={`${styles.background} ${props.imageClassName || ''}`} style={backgroundStyle}></div>
        )
      }
      <div className={styles.media}>
        {
          !invalidUrl && !loaded && (
            <div className={`${styles.loader} ${styles.center}`} data-testid="gif-loader">
              <CircularProgress className={styles['loader-icon']} size={props.small ? LOADER_SIZE_SMALL : LOADER_SIZE} />
            </div>
          )
        }
        {loaded && gifSign}
        {
          isVideo && (
            <video
              controlsList="nodownload nofullscreen noremoteplayback"
              ref={videoRef}
              loop={Boolean(props.loop)}
              muted
              preload="metadata"
              data-testid={props.mediaElementId}
              className={`${styles.video} ${styles.center}`}
              onMouseEnter={playVideo}
              onMouseLeave={pauseVideo}
              onLoadedMetadata={onMediaLoaded}
              onCanPlay={onMediaLoaded}
              onLoadedData={onMediaLoaded}
              onError={onMediaLoaded}
            >
              <source src={mediaUrl} />
              <p>Your browser does not support videos! Please try another one.</p>
            </video>
          )
        }
        {
          isGif && (
            <div
              ref={gifRef}
              className={`freezeframe ${styles.gif}`}
            >
              <img
                src={mediaUrl}
                data-testid={props.mediaElementId}
              />
            </div>
          )
        }
      </div>
    </div>
  )
}

export default GifPlayer
